import { withCookieFirstRequest } from '../../shared/api/cookieAuthRequest'
import type { CustomerPasskeyCredential } from '../types'
import { requestCustomerAuthJson } from './core'

const PASSKEY_LIST_PATH = '/passkeys'
const PASSKEY_REGISTER_OPTIONS_PATH = '/passkeys/register/options'
const PASSKEY_REGISTER_VERIFY_PATH = '/passkeys/register/verify'

const toPasskeyCredential = (value: unknown): CustomerPasskeyCredential | null => {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  if (typeof raw.id !== 'string' || !raw.id.trim()) return null
  return {
    id: raw.id.trim(),
    nickname: typeof raw.nickname === 'string' && raw.nickname.trim() ? raw.nickname.trim() : null,
    createdAt: typeof raw.createdAt === 'string' && raw.createdAt.trim() ? raw.createdAt.trim() : null,
  }
}

const normalizePasskeys = (payload: Record<string, unknown>): CustomerPasskeyCredential[] => {
  const root = payload.passkeys
  const list =
    root && typeof root === 'object' && Array.isArray((root as { credentials?: unknown }).credentials)
      ? ((root as { credentials: unknown[] }).credentials ?? [])
      : []

  return list.map(toPasskeyCredential).filter((item): item is CustomerPasskeyCredential => item !== null)
}

export const fetchCustomerPasskeys = async (token?: string | null): Promise<CustomerPasskeyCredential[]> => {
  const payload = await requestCustomerAuthJson<Record<string, unknown>>(PASSKEY_LIST_PATH, {
    ...withCookieFirstRequest({
      init: {
        method: 'GET',
      },
      bearerFallbackToken: token,
    }),
  })
  return normalizePasskeys(payload)
}

export const requestCustomerPasskeyRegisterOptions = (token?: string | null) =>
  requestCustomerAuthJson<Record<string, unknown>>(PASSKEY_REGISTER_OPTIONS_PATH, {
    ...withCookieFirstRequest({
      init: {
        method: 'POST',
      },
      bearerFallbackToken: token,
    }),
  })

export const verifyCustomerPasskeyRegistration = (
  input: { response: Record<string, unknown>; nickname?: string },
  token?: string | null,
) =>
  requestCustomerAuthJson<Record<string, unknown>>(PASSKEY_REGISTER_VERIFY_PATH, {
    ...withCookieFirstRequest({
      init: {
        method: 'POST',
        body: JSON.stringify({
          response: input.response,
          nickname: input.nickname?.trim() || undefined,
        }),
      },
      bearerFallbackToken: token,
    }),
  })

export const removeCustomerPasskey = (passkeyId: string, token?: string | null) => {
  const encoded = encodeURIComponent(passkeyId)
  return requestCustomerAuthJson<Record<string, unknown>>(`/passkeys/${encoded}`, {
    ...withCookieFirstRequest({
      init: {
        method: 'DELETE',
      },
      bearerFallbackToken: token,
    }),
  })
}
